import React from 'react';
import { CheckCircle, Calendar, User, Mail, Phone, Shield, Clock, FileText, DollarSign, Award, AlertCircle } from 'lucide-react';
import TaxPreparationForm from './TaxPreparationForm';

const TaxPreparationTemplate: React.FC = () => {
  const benefits = [
    {
      icon: <Award className="h-10 w-10 text-primary" />,
      title: "Retirement Income Expertise",
      description: "Specialized knowledge in handling retirement accounts, Social Security benefits, and pension distributions."
    },
    {
      icon: <Shield className="h-10 w-10 text-primary" />,
      title: "Senior Tax Deductions",
      description: "Maximize your tax savings with deductions and credits specifically available to those 55 and older."
    },
    {
      icon: <FileText className="h-10 w-10 text-primary" />,
      title: "Healthcare Expense Reporting",
      description: "Proper handling of Medicare, supplemental insurance, and medical expense deductions."
    },
    {
      icon: <DollarSign className="h-10 w-10 text-primary" />,
      title: "Required Minimum Distributions",
      description: "Expert guidance on RMD rules and tax implications for your retirement accounts."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div>
            <div className="inline-flex items-center bg-primary/10 rounded-full px-4 py-2 text-primary mb-6">
              <DollarSign className="h-5 w-5 mr-2" />
              Limited Time Offer
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Professional Tax Return Preparation
              <span className="block text-primary">Just $99 for Seniors</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Get your taxes done right by experts who understand the unique tax situations facing adults 55 and older. Our professional preparers will help you maximize deductions and minimize stress.
            </p>
            
            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
              <div className="flex">
                <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Limited Time Offer</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>This special $99 rate is only available for a limited time and for adults 55 and older. Regular price is $199. Offer valid for basic tax returns only. Additional forms may incur extra charges.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Form */}
          <TaxPreparationForm onPreview={true} />
        </div>
      </div>
      
      {/* Trust Indicators Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose Our Tax Preparation Services?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Certified Experts</h3>
              <p className="text-gray-600">All tax preparers are IRS Enrolled Agents or CPAs with senior tax expertise</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">25+ Years Experience</h3>
              <p className="text-gray-600">Decades of experience helping seniors maximize their tax benefits</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">100% Satisfaction</h3>
              <p className="text-gray-600">Guaranteed accuracy or we'll refund your preparation fee</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <DollarSign className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Senior Discount</h3>
              <p className="text-gray-600">Special $99 rate exclusively for adults 55 and older</p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <div className="inline-flex flex-wrap justify-center gap-8">
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/IRS_logo.svg/1200px-IRS_logo.svg.png" alt="IRS" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/AICPA_logo.svg/2560px-AICPA_logo.svg.png" alt="AICPA" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Better_Business_Bureau.svg/1200px-Better_Business_Bureau.svg.png" alt="BBB" className="h-12 w-auto grayscale opacity-70" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Terms and Conditions */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms and Conditions</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                The $99 tax preparation offer is valid for adults 55 years of age or older for basic tax returns only. 
                A basic tax return includes Form 1040 and Schedules A and B.
              </p>
              <p>
                Additional forms and schedules may incur extra charges. Complex returns involving business income, 
                rental properties, or extensive investment transactions are not eligible for this offer.
              </p>
              <p>
                This offer cannot be combined with other discounts or promotions. Valid for the 2024 tax filing season only.
                Offer subject to change without notice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxPreparationTemplate;